import { zodResolver } from '@hookform/resolvers/zod';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import PostAddRounded from '@mui/icons-material/PostAddRounded';
import {
  Alert,
  Box,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PageHeader } from '../../components/states';
import { businessDate, money, qty as fmtQty } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { fromUnits, toUnits } from '../payments/allocation';
import { idempotency, invalidateMoney } from '../payments/kit/hooks';
import { links, todayIn } from '../payments/kit/labels';
import { BackLink, DetailSkeleton, ErrorState, SectionCard } from '../payments/kit/ui';
import { useNotesFor, useSalesInvoice, useSupplierInvoice, type NoteKind } from './api';
import { checkDraft, creditable, usageByLine } from './calc';
import {
  FormGrid,
  GhostButton,
  HeaderFields,
  LineInput,
  PickSourceCard,
  SummaryPanel,
} from './kit';
import { noteHeaderSchema, type NoteHeaderValues } from './schemas';

const TEXT = {
  credit: {
    title: 'New credit note',
    param: 'invoiceId',
    source: 'invoice',
    pickTitle: 'Which invoice are you crediting?',
    pickBody:
      'A credit note lowers what the customer owes without goods coming back — a price adjustment, a missed discount, a damaged-in-transit allowance.',
    party: 'customer',
    back: '/returns?tab=credit-notes',
  },
  debit: {
    title: 'New debit note',
    param: 'billId',
    source: 'bill',
    pickTitle: 'Which bill did the supplier credit?',
    pickBody:
      'A debit note records a credit the supplier gave you on a bill without goods going back — a price correction or an allowance.',
    party: 'supplier',
    back: '/returns?tab=debit-notes',
  },
} as const;

interface SourceDoc {
  id: string;
  number: string;
  extra: string | null;
  contact: { name: string };
  docDate: string;
  status: string;
  total: string;
  outstanding: string;
  lines: {
    id: string;
    description: string;
    sku: string | null;
    qty: string;
    unitSymbol: string | null;
    lineTotal: string;
  }[];
}

/** Credit an invoice (credit note) or record a supplier's credit on a bill (debit note). */
export function NoteFormPage({ kind }: { kind: NoteKind }) {
  const t = TEXT[kind];
  const [params, setParams] = useSearchParams();
  const sourceId = params.get(t.param);
  if (!sourceId)
    return (
      <>
        <BackLink to={t.back} label="Returns & notes" />
        <PageHeader title={t.title} />
        <PickSourceCard
          kind={kind === 'credit' ? 'salesInvoice' : 'supplierInvoice'}
          title={t.pickTitle}
          body={t.pickBody}
          onPick={(o) => setParams({ [t.param]: o.id })}
        />
      </>
    );
  return <NoteForm kind={kind} sourceId={sourceId} />;
}

function useSource(kind: NoteKind, id: string) {
  const inv = useSalesInvoice(kind === 'credit' ? id : null);
  const bill = useSupplierInvoice(kind === 'debit' ? id : null);
  const q = kind === 'credit' ? inv : bill;
  let data: SourceDoc | undefined;
  if (kind === 'credit' && inv.data)
    data = { ...inv.data, extra: null, lines: inv.data.lines.map((l) => ({ ...l })) };
  if (kind === 'debit' && bill.data)
    data = {
      ...bill.data,
      extra: bill.data.supplierInvoiceNumber,
      lines: bill.data.lines.map((l) => ({ ...l })),
    };
  return { data, isLoading: q.isLoading, error: q.error, refetch: q.refetch };
}

function NoteForm({ kind, sourceId }: { kind: NoteKind; sourceId: string }) {
  const t = TEXT[kind];
  const { format } = useShop();
  const d = format.currencyDecimals;
  const { can } = useAuth();
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const source = useSource(kind, sourceId);
  const prior = useNotesFor(kind, sourceId);
  const usage = useMemo(() => usageByLine(prior.notes), [prior.notes]);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<NoteHeaderValues | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<NoteHeaderValues>({
    resolver: zodResolver(noteHeaderSchema),
    defaultValues: { docDate: todayIn(format.timeZone), reason: '', notes: '' },
  });

  const rows = (source.data?.lines ?? []).map((l) => {
    const credited = usage.get(l.id)?.credited ?? 0n;
    return {
      line: l,
      credited,
      max: creditable(l.lineTotal, credited),
      value: amounts[l.id] ?? '',
    };
  });
  const check = checkDraft(
    rows.map((r) => ({ lineId: r.line.id, max: r.max, value: r.value })),
    { scale: d, overMessage: 'More than is left to credit' },
  );
  const total = rows.reduce(
    (s, r) => s + (check.errors[r.line.id] ? 0n : (toUnits(r.value) ?? 0n)),
    0n,
  );

  const save = useMutation({
    mutationFn: async (v: NoteHeaderValues) => {
      const filled = rows.filter((r) => (toUnits(r.value) ?? 0n) > 0n);
      const common = {
        docDate: v.docDate,
        reason: v.reason,
        notes: v.notes || null,
      };
      if (kind === 'credit') {
        const n = await unwrap(
          api.POST('/credit-notes', {
            params: idempotency(),
            body: {
              ...common,
              salesInvoiceId: sourceId,
              lines: filled.map((r) => ({ invoiceLineId: r.line.id, amount: r.value.trim() })),
            },
          }),
        );
        return { id: n.id, number: n.number, total: n.total };
      }
      const n = await unwrap(
        api.POST('/debit-notes', {
          params: idempotency(),
          body: {
            ...common,
            supplierInvoiceId: sourceId,
            lines: filled.map((r) => ({
              supplierInvoiceLineId: r.line.id,
              amount: r.value.trim(),
            })),
          },
        }),
      );
      return { id: n.id, number: n.number, total: n.total };
    },
    onSuccess: async (n) => {
      notify.success(`${n.number} posted for ${money(n.total, format)}`);
      await invalidateMoney(qc);
      void navigate(kind === 'credit' ? links.creditNote(n.id) : links.debitNote(n.id));
    },
    onError: (e) => {
      if (e instanceof ApiError)
        for (const det of e.details) {
          const field = det.path.split('.').pop();
          if (field === 'docDate' || field === 'reason' || field === 'notes')
            setError(field, { message: det.message });
        }
      notify.error(e);
    },
  });

  if (source.isLoading || prior.loading) return <DetailSkeleton />;
  if (!source.data || prior.error)
    return (
      <>
        <BackLink to={t.back} label="Returns & notes" />
        <ErrorState error={source.error ?? prior.error} onRetry={() => void source.refetch()} />
      </>
    );

  const doc = source.data;
  const posted = doc.status === 'POSTED';
  const allowed = can(kind === 'credit' ? 'sales.return' : 'purchases.return');
  const totalStr = fromUnits(total, d);
  const outstanding = toUnits(doc.outstanding) ?? 0n;
  const sourceLink = kind === 'credit' ? links.salesInvoice(doc.id) : links.supplierInvoice(doc.id);

  return (
    <>
      <BackLink to={t.back} label="Returns & notes" />
      <PageHeader
        title={t.title}
        subtitle={
          <>
            Against{' '}
            <Link component={RouterLink} to={sourceLink} sx={{ fontWeight: 650 }}>
              {doc.number}
            </Link>
            {doc.extra ? ` (${doc.extra})` : ''} · {doc.contact.name} ·{' '}
            {businessDate(doc.docDate, format)}
          </>
        }
        actions={
          <Button
            variant="outlined"
            onClick={() =>
              void navigate(`/returns/${kind}-notes/new`, {
                replace: true,
              })
            }
          >
            Change {t.source}
          </Button>
        }
      />
      {!posted && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {doc.number} is {doc.status.toLowerCase()} — only posted documents can be credited.
        </Alert>
      )}
      {!allowed && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          You don’t have permission to post {kind} notes.
        </Alert>
      )}
      <form
        noValidate
        onSubmit={(e) =>
          void handleSubmit((v) => {
            if (check.valid) setPending(v);
          })(e)
        }
      >
        <FormGrid
          main={
            <>
              <SectionCard
                flush
                title="Amounts to credit"
                subtitle="Gross amounts (tax included); each line is split into net and tax in its own proportion"
                action={
                  <GhostButton
                    onClick={() =>
                      setAmounts(
                        Object.fromEntries(
                          rows.map((r) => [r.line.id, r.max > 0n ? fromUnits(r.max, d) : '']),
                        ),
                      )
                    }
                  >
                    <DoneAllRounded fontSize="small" sx={{ mr: 0.75 }} />
                    Credit everything left
                  </GhostButton>
                }
              >
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Line</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          Line total
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          Left to credit
                        </TableCell>
                        <TableCell align="right">Credit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow
                          key={r.line.id}
                          sx={{ opacity: r.max === 0n ? 0.55 : 1, verticalAlign: 'top' }}
                        >
                          <TableCell sx={{ pt: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {r.line.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[
                                r.line.sku,
                                `${fmtQty(r.line.qty, format)} ${r.line.unitSymbol ?? ''}`.trim(),
                                r.credited > 0n
                                  ? `${money(fromUnits(r.credited, d), format)} credited before`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            className="tabular"
                            sx={{ display: { xs: 'none', md: 'table-cell' }, pt: 1.5 }}
                          >
                            {money(r.line.lineTotal, format)}
                          </TableCell>
                          <TableCell
                            align="right"
                            className="tabular"
                            sx={{
                              display: { xs: 'none', sm: 'table-cell' },
                              pt: 1.5,
                              fontWeight: 600,
                            }}
                          >
                            {money(fromUnits(r.max, d), format)}
                          </TableCell>
                          <TableCell align="right">
                            <LineInput
                              value={r.value}
                              onChange={(v) => setAmounts((p) => ({ ...p, [r.line.id]: v }))}
                              error={check.errors[r.line.id]}
                              label={`Credit for ${r.line.description}`}
                              prefix={format.currencyCode}
                              width={150}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
              <SectionCard title="Details">
                <HeaderFields
                  register={register}
                  errors={errors}
                  reasonRequired
                  reasonPlaceholder={
                    kind === 'credit'
                      ? 'e.g. agreed price correction'
                      : 'e.g. supplier allowed 5% for late delivery'
                  }
                />
              </SectionCard>
            </>
          }
          side={
            <SummaryPanel
              title="Summary"
              rows={[
                {
                  label: kind === 'credit' ? 'Invoice total' : 'Bill total',
                  value: money(doc.total, format),
                },
                { label: 'Outstanding', value: money(doc.outstanding, format) },
                { label: 'Lines credited', value: String(check.count) },
              ]}
              total={{
                label: kind === 'credit' ? 'Credit note' : 'Debit note',
                value: money(totalStr, format),
              }}
              explain={
                <>
                  Settles {doc.number} first
                  {total > outstanding
                    ? `; the ${money(fromUnits(total - outstanding, d), format)} above what is still open goes to the ${t.party}’s oldest open documents, and any rest stays as credit.`
                    : '.'}{' '}
                  No stock moves. You can cancel it later as long as none of it was refunded.
                </>
              }
              actions={
                <>
                  <Button variant="outlined" onClick={() => void navigate(t.back)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<PostAddRounded />}
                    disabled={!allowed || !posted || !check.valid}
                  >
                    Post {kind} note
                  </Button>
                </>
              }
            />
          }
        />
      </form>
      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title={`Post this ${kind} note?`}
        confirmLabel={`Post ${kind} note`}
        body={
          <Box component="span">
            <b>{money(totalStr, format)}</b> credited on {doc.number} for {doc.contact.name}. It
            posts to the ledger right away.
          </Box>
        }
        onConfirm={() =>
          pending ? save.mutateAsync(pending).catch(() => undefined) : Promise.resolve()
        }
      />
    </>
  );
}
